/**
 * Constants for blueprint/template functionality
 */

/**
 * Special section ID for basic information visibility control
 * This is a pseudo-section that controls whether users can see/edit
 * the template's basic information (name, description, tags, etc.)
 */
export const BASIC_INFO_SECTION_ID = '__basic_information__';

/**
 * Check if basic information is visible for a given role
 * @param template - The template/blueprint data
 * @param roleId - The role ID to check
 * @returns true if basic info should be visible for this role
 */
export function isBasicInfoVisibleForRole(
  template: any,
  roleId: string | null
): boolean {
  if (!template || !roleId) return true; // Default to visible if no data
  
  // Check if there's a basic info section with visibility settings
  const basicInfoSection = template.data?.sections?.[BASIC_INFO_SECTION_ID];
  if (!basicInfoSection) return true; // Default to visible if not configured
  
  // Check if role is in the hidden list
  const hiddenForRoles = basicInfoSection.hiddenForRoles || [];
  return !hiddenForRoles.includes(roleId);
}

/**
 * Initialize basic information section with default visibility settings
 * (Owner can see, all other roles cannot by default)
 * @param ownerRoleId - The ID of the Owner role
 * @param allRoleIds - Array of all role IDs
 * @returns The basic info section configuration
 */
export function initializeBasicInfoSection(
  ownerRoleId: string,
  allRoleIds: string[]
): any {
  // Hide for all roles except owner
  const hiddenForRoles = allRoleIds.filter(id => id !== ownerRoleId);
  
  return {
    _sectionTitle: 'Basic Information',
    hiddenForRoles,
  };
}

/**
 * Ensure a template has the basic info section initialized
 * If it doesn't exist, create it with default visibility (Owner can see, others cannot)
 * @param template - The template to check/update
 * @param ownerRoleId - The ID of the Owner role
 * @param allRoleIds - Array of all role IDs
 * @returns Updated template with basic info section
 */
export function ensureBasicInfoSection(
  template: any,
  ownerRoleId: string,
  allRoleIds: string[]
): any {
  if (!template?.data?.sections) return template;
  
  // If basic info section doesn't exist, initialize it
  if (!template.data.sections[BASIC_INFO_SECTION_ID]) {
    return {
      ...template,
      data: {
        ...template.data,
        sections: {
          ...template.data.sections,
          [BASIC_INFO_SECTION_ID]: initializeBasicInfoSection(ownerRoleId, allRoleIds),
        },
      },
    };
  }
  
  return template;
}
